import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { getNatMutControl, getNatMutGroup, getSchedaPrevalenzaControl, natMutCkHelper } from './helpers/natura-mutualistica-form-validators.helper';

export const naturaMutualisticaMutualitaPrevalenteRules: FormGroupRules = {
    my_CkEsisteRipDividSociMutNP: (form: FormGroup) => {
        natMutCkHelper(form, 'my_CkEsisteRipDividSociMutNP', 'my_SchedaPrevalenza.my_CkIscrizAlboCoopMutP', 2);
    },

    my_CkRipDividSociMutNP: (form: FormGroup) => {
        const my_CkIscrizAlboCoopMutP: string = getSchedaPrevalenzaControl(form, 'my_CkIscrizAlboCoopMutP')?.value;
        const suppCondition = my_CkIscrizAlboCoopMutP === '2';
        natMutCkHelper(form, 'my_CkRipDividSociMutNP', 'my_CkEsisteRipDividSociMutNP', 1, undefined, suppCondition);
    },

    my_CkCostituiteRiserveDivisibili: (form: FormGroup) => {
        natMutCkHelper(form, 'my_CkCostituiteRiserveDivisibili', 'my_SchedaPrevalenza.my_CkIscrizAlboCoopMutP', 2);
    },

    my_CkRiserveIndivis: (form: FormGroup) => {
        const my_CkIscrizAlboCoopMutP: string = getSchedaPrevalenzaControl(form, 'my_CkIscrizAlboCoopMutP')?.value;
        const suppCondition = my_CkIscrizAlboCoopMutP === '2';
        natMutCkHelper(form, 'my_CkRiserveIndivis', 'my_CkCostituiteRiserveDivisibili', 1, undefined, suppCondition);
    },

    my_CkGestSeparata: (form: FormGroup) => {
        const my_CkIscrizAlboCoopMutP: string = getSchedaPrevalenzaControl(form, 'my_CkIscrizAlboCoopMutP')?.value;
        const suppCondition = my_CkIscrizAlboCoopMutP === '2';
        natMutCkHelper(form, 'my_CkGestSeparata', 'my_CkCostituiteRiserveDivisibili', 1, undefined, suppCondition);
    },

    my_CkRiserveDivisibiliRipartite: (form: FormGroup) => {
        const naturaMutualisticaGroup = getNatMutGroup(form);
        const my_CkIscrizAlboCoopMutP: string = getControl(naturaMutualisticaGroup, 'my_SchedaPrevalenza.my_CkIscrizAlboCoopMutP')?.value;
        const suppCondition = my_CkIscrizAlboCoopMutP === '2';
        natMutCkHelper(form, 'my_CkRiserveDivisibiliRipartite', 'my_CkCostituiteRiserveDivisibili', 1, undefined, suppCondition);
    },

    my_CkRiserveDivisibiliRispetPrevision: (form: FormGroup) => {
        const naturaMutualisticaGroup = getNatMutGroup(form);
        const my_CkIscrizAlboCoopMutP: string = getControl(naturaMutualisticaGroup, 'my_SchedaPrevalenza.my_CkIscrizAlboCoopMutP')?.value;
        const my_CkRiserveDivisibiliRipartite: string = getControl(naturaMutualisticaGroup, 'my_CkRiserveDivisibiliRipartite')?.value;
        const suppCondition = my_CkIscrizAlboCoopMutP === '2' && my_CkRiserveDivisibiliRipartite === '1';
        natMutCkHelper(form, 'my_CkRiserveDivisibiliRispetPrevision', 'my_CkCostituiteRiserveDivisibili', 1, undefined, suppCondition);
    },

    my_CkRispCritPrev: (form: FormGroup) => {
        ckMutualitaPrevalenteHelper(form, 'my_CkRispCritPrev');
    },

    my_CkSonoStatiRipDividSociMutP: (form: FormGroup) => {
        ckMutualitaPrevalenteHelper(form, 'my_CkSonoStatiRipDividSociMutP');
    },

    my_CkRipDividSociMutP: (form: FormGroup) => {
        const my_CkSonoStatiRipDividSociMutP: string = getNatMutControl(form, 'my_CkSonoStatiRipDividSociMutP')?.value;
        ckMutualitaPrevalenteHelper(form, 'my_CkRipDividSociMutP', my_CkSonoStatiRipDividSociMutP === '1');
    },

    my_CkEmessoStrumFinanziari: (form: FormGroup) => {
        ckMutualitaPrevalenteHelper(form, 'my_CkEmessoStrumFinanziari');
    },

    my_CkRemuneratiStrumFinanziari: (form: FormGroup) => {
        const my_CkEmessoStrumFinanziari: string = getNatMutControl(form, 'my_CkEmessoStrumFinanziari')?.value;
        ckMutualitaPrevalenteHelper(form, 'my_CkRemuneratiStrumFinanziari', my_CkEmessoStrumFinanziari === '1');
    },

    my_CkRispettoGestStrumFinanziari: (form: FormGroup) => {
        const my_CkEmessoStrumFinanziari: string = getNatMutControl(form, 'my_CkEmessoStrumFinanziari')?.value;
        const my_CkRemuneratiStrumFinanziari: string = getNatMutControl(form, 'my_CkRemuneratiStrumFinanziari')?.value;
        ckMutualitaPrevalenteHelper(form, 'my_CkRispettoGestStrumFinanziari', my_CkEmessoStrumFinanziari === '1' && my_CkRemuneratiStrumFinanziari === '1');
    },
};

/** Valida campo obbligatorio per cooperativa iscritta all'albo come mutualità prevalente */
const ckMutualitaPrevalenteHelper = (form: FormGroup, targetPath: string, suppCondition = true): void => {
    const naturaMutualisticaGroup = getNatMutGroup(form);
    const my_CkIscrizAlboCoopMutP: string = getControl(naturaMutualisticaGroup, 'my_SchedaPrevalenza.my_CkIscrizAlboCoopMutP')?.value;
    const targetControl = getControl(naturaMutualisticaGroup, targetPath);
    const errors = targetControl.errors ? targetControl.errors : {};
    const showError = isNilOrEmpty(targetControl?.value) && (my_CkIscrizAlboCoopMutP === '1' || my_CkIscrizAlboCoopMutP === '4') && suppCondition;

    addRemoveError(showError, errors, targetPath, { status: REQUIRED_STATUS });
    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};
